import clsx from 'clsx';
import { memo } from 'react';

import type { SkeletonProps, SkeletonSize } from './types';

import { Layout } from '../Layout/Layout';
import './Skeleton.css';

const FORM_FIELD_COUNT = 3;
const PARAGRAPH_LINE_COUNT = 4;
const TABLE_ROW_COUNT = 4;
const TABLE_COLUMN_COUNT = 4;

/* A ragged last line, not a design token — it keeps a stack of bars reading as
   prose rather than as a solid slab. */
const componentStyles = {
  formPiece: { width: 'var(--eds--size-96)' },
  fullWidth: { width: '100%' },
  paragraphLastLine: { width: '60%' },
} as const;

/** `fill` has no meaning inside a composed shape, which sizes its own pieces. */
const resolveLineSize = (size: SkeletonSize) => (size === 'fill' ? 'm' : size);

export const Skeleton = memo((props: SkeletonProps) => {
  const { label, radius = 's', shape = 'rect', size = 'm' } = props;

  /* A blank label would announce a status region with no accessible name, so
     treat it the same as an omitted one. */
  const accessibleName = label?.trim() ? label : undefined;
  const statusRole = accessibleName ? 'status' : undefined;
  /* Without a name the placeholder is purely decorative — keep it out of the
     accessibility tree rather than exposing anonymous nodes (one bar per line,
     sixteen per table). Mirrors Icon. */
  const ariaHidden = accessibleName === undefined ? true : undefined;

  if (shape === 'avatar') {
    return (
      <div
        aria-hidden={ariaHidden}
        aria-label={accessibleName}
        className="eds-skeleton eds-skeleton--avatar"
        role={statusRole}
      />
    );
  }

  if (shape === 'paragraph') {
    const lineSize = resolveLineSize(size);

    return (
      <Layout
        aria-hidden={ariaHidden}
        aria-label={accessibleName}
        direction="column"
        role={statusRole}
        spacing="xs"
        style={componentStyles.fullWidth}
      >
        {Array.from({ length: PARAGRAPH_LINE_COUNT }).map((_, index) => (
          <div
            key={index}
            style={
              index === PARAGRAPH_LINE_COUNT - 1
                ? componentStyles.paragraphLastLine
                : componentStyles.fullWidth
            }
          >
            <Skeleton radius={radius} size={lineSize} />
          </div>
        ))}
      </Layout>
    );
  }

  if (shape === 'form') {
    /* Mirrors a body-s field label over a control-height input. */
    return (
      <Layout
        aria-hidden={ariaHidden}
        aria-label={accessibleName}
        direction="column"
        role={statusRole}
        spacing="m"
        style={componentStyles.fullWidth}
      >
        {Array.from({ length: FORM_FIELD_COUNT }).map((_, index) => (
          <Layout key={index} direction="column" spacing="xs">
            <div style={componentStyles.formPiece}>
              <Skeleton radius={radius} size="s" />
            </div>
            <Skeleton radius={radius} size="xl" />
          </Layout>
        ))}
        <div style={componentStyles.formPiece}>
          <Skeleton radius={radius} size="xl" />
        </div>
      </Layout>
    );
  }

  if (shape === 'table') {
    const cellSize = resolveLineSize(size);

    return (
      <Layout
        aria-hidden={ariaHidden}
        aria-label={accessibleName}
        direction="column"
        role={statusRole}
        spacing="s"
        style={componentStyles.fullWidth}
      >
        {Array.from({ length: TABLE_ROW_COUNT }).map((_, rowIndex) => (
          <Layout key={rowIndex} spacing="s">
            {Array.from({ length: TABLE_COLUMN_COUNT }).map((_, colIndex) => (
              <Layout key={colIndex} flex="1">
                <Skeleton radius={radius} size={cellSize} />
              </Layout>
            ))}
          </Layout>
        ))}
      </Layout>
    );
  }

  return (
    <div
      aria-hidden={ariaHidden}
      aria-label={accessibleName}
      className={clsx(
        'eds-skeleton',
        `eds-skeleton--radius-${radius}`,
        `eds-skeleton--size-${size}`,
      )}
      role={statusRole}
    />
  );
});

Skeleton.displayName = 'Skeleton';
